import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Helper function to prepare data for export
const prepareDataForExport = (appointments, userNotes) => {
  return appointments.map((appointment, index) => {
    const appointmentKey = `${appointment.campaignId}_${appointment.id}`;
    const notes = userNotes[appointmentKey] || {};
    
    return {
      'Nr.': index + 1,
      'Campanie': appointment.campaignName || '',
      'Nume': appointment.name || '',
      'Prenume': appointment.lastname || '',
      'Telefon': appointment.phone || '',
      'Oraș': appointment.city || '',
      'Încercat Uleiuri': appointment.hasTriedEssentialOils || '',
      'Cont doTERRA': appointment.hasDoTerraAccount || '',
      'Provocări': notes.provocari || '',
      'Mostre': notes.mostre || '',
      'Înscris': notes.inscris ? 'Da' : 'Nu',
      'Data Programării': appointment.createDate ? new Date(appointment.createDate).toLocaleDateString('ro-RO') : ''
    };
  });
};

// Helper to remove Romanian diacritics for PDF export
const removeRomanianDiacritics = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/[âǎă]/g, 'a')
    .replace(/[șş]/g, 's')
    .replace(/[țţ]/g, 't')
    .replace(/[î]/g, 'i');
};

// Export to Excel (XLSX)
export const exportToExcel = (appointments, userNotes, filename = 'mostre_export') => {
  try {
    const data = prepareDataForExport(appointments, userNotes);
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Set column widths
    const columnWidths = [
      { wch: 5 },   // Nr.
      { wch: 15 },  // Campanie
      { wch: 15 },  // Nume
      { wch: 15 },  // Prenume
      { wch: 15 },  // Telefon
      { wch: 12 },  // Oraș
      { wch: 12 },  // Încercat Uleiuri
      { wch: 12 },  // Cont doTERRA
      { wch: 30 },  // Provocări
      { wch: 30 },  // Mostre
      { wch: 8 },   // Înscris
      { wch: 12 }   // Data
    ];
    ws['!cols'] = columnWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Mostre');
    
    // Generate Excel file and save
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const timestamp = new Date().toISOString().slice(0, 10);
    saveAs(dataBlob, `${filename}_${timestamp}.xlsx`);
    
    return { success: true, message: 'Fișierul Excel a fost descărcat cu succes!' };
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return { success: false, message: 'Eroare la exportul în Excel!' };
  }
};

// Export to CSV
export const exportToCSV = (appointments, userNotes, filename = 'mostre_export') => {
  try {
    const data = prepareDataForExport(appointments, userNotes);
    
    // Create CSV headers
    const headers = Object.keys(data[0]);
    
    // Convert data to CSV format
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header] || '';
          // Escape commas and quotes in values
          return `"${value.toString().replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');
    
    // Create blob and save
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
    const timestamp = new Date().toISOString().slice(0, 10);
    saveAs(blob, `${filename}_${timestamp}.csv`);
    
    return { success: true, message: 'Fișierul CSV a fost descărcat cu succes!' };
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    return { success: false, message: 'Eroare la exportul în CSV!' };
  }
};

// Export to PDF
export const exportToPDF = (appointments, userNotes, filename = 'mostre_export') => {
  try {
    const data = prepareDataForExport(appointments, userNotes);
    // Remove diacritics for all fields in PDF export only
    const dataNoDiacritics = data.map(row => {
      const newRow = {};
      Object.keys(row).forEach(key => {
        newRow[key] = removeRomanianDiacritics(row[key]);
      });
      return newRow;
    });
    // Create new PDF document
    const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation
    
    // Add title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Raport Mostre - The Oils Babe', 20, 20);
    
    // Add export date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Exportat la: ${new Date().toLocaleDateString('ro-RO')} ${new Date().toLocaleTimeString('ro-RO')}`, 20, 30);
    doc.text(`Total intrari: ${dataNoDiacritics.length}`, 20, 35);
    
    // Prepare table data
    const tableColumns = [
      'Nr.', 'Campanie', 'Nume', 'Prenume', 'Telefon', 'Oras', 
      'Incercat', 'Cont', 'Provocari', 'Mostre', 'Inscris', 'Data'
    ];
    
    const tableRows = dataNoDiacritics.map(row => [
      row['Nr.'],
      row['Campanie'],
      row['Nume'],
      row['Prenume'],
      row['Telefon'],
      row['Oraș'],
      row['Încercat Uleiuri'],
      row['Cont doTERRA'],
      (row['Provocări'] || '').substring(0, 50) + ((row['Provocări'] || '').length > 50 ? '...' : ''),
      (row['Mostre'] || '').substring(0, 30) + ((row['Mostre'] || '').length > 30 ? '...' : ''),
      row['Înscris'],
      row['Data Programării']
    ]);
    
    // Add table using jsPDF autoTable
    doc.autoTable({
      head: [tableColumns],
      body: tableRows,
      startY: 45,
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [144, 77, 113], // Theme color
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 10 },  // Nr.
        1: { cellWidth: 25 },  // Campanie
        2: { cellWidth: 20 },  // Nume
        3: { cellWidth: 20 },  // Prenume
        4: { cellWidth: 25 },  // Telefon
        5: { cellWidth: 20 },  // Oras
        6: { cellWidth: 15 },  // Incercat
        7: { cellWidth: 15 },  // Cont
        8: { cellWidth: 50 },  // Provocari
        9: { cellWidth: 40 },  // Mostre
        10: { cellWidth: 15 }, // Inscris
        11: { cellWidth: 20 }  // Data
      },
      margin: { top: 45, left: 10, right: 10 },
      didDrawPage: function (data) {
        // Add page numbers
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(`Pagina ${data.pageNumber} din ${pageCount}`,
          doc.internal.pageSize.width - 30,
          doc.internal.pageSize.height - 10);
      }
    });
    
    // Save the PDF
    const timestamp = new Date().toISOString().slice(0, 10);
    doc.save(`${filename}_${timestamp}.pdf`);
    
    return { success: true, message: 'Fișierul PDF a fost descărcat cu succes!' };
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    return { success: false, message: 'Eroare la exportul în PDF!' };
  }
};

// Export to JSON (for backup purposes)
export const exportToJSON = (appointments, userNotes, filename = 'mostre_export') => {
  try {
    const data = {
      exportDate: new Date().toISOString(),
      totalRecords: appointments.length,
      appointments: appointments,
      userNotes: userNotes
    };
    
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    
    const timestamp = new Date().toISOString().slice(0, 10);
    saveAs(blob, `${filename}_backup_${timestamp}.json`);
    
    return { success: true, message: 'Backup-ul JSON a fost descărcat cu succes!' };
  } catch (error) {
    console.error('Error exporting to JSON:', error);
    return { success: false, message: 'Eroare la exportul JSON!' };
  }
};

// Get export statistics
export const getExportStats = (appointments, userNotes) => {
  const enrolledCount = Object.values(userNotes).filter(note => note.inscris).length;
  const withChallenges = Object.values(userNotes).filter(note => note.provocari && note.provocari.trim()).length;
  const withSamples = Object.values(userNotes).filter(note => note.mostre && note.mostre.trim()).length;
  const campaignCounts = {};
  
  appointments.forEach(apt => {
    campaignCounts[apt.campaignName] = (campaignCounts[apt.campaignName] || 0) + 1;
  });
  
  return {
    total: appointments.length,
    enrolled: enrolledCount,
    withChallenges,
    withSamples,
    campaigns: Object.keys(campaignCounts).length,
    campaignCounts
  };
};
